/** @file Picks the bone under a screen point: joint disc, tip disc or body segment, in screen pixels. */
import { affine } from '@pose2d/math/affine';
import { trig } from '@pose2d/math/trig';
import type { Vec2 } from '@pose2d/math/types';
import { POSE_LAYOUT } from '@pose2d/core/contracts/pose';
import type { BoneData } from '@pose2d/core/types';
import type { Camera } from '@pose2d/geometry/types';
import { pointSegmentDistance } from './_internal/pointSegmentDistance';
import { readNum } from './_internal/readNum';
import { HANDLE_PX } from './handlePx';

/** What pickBone reads: world matrices from a Pose, bone lengths, the camera and an optional pickable mask. */
export interface BonePickInput {
  /** `Pose.world`: WORLD_STRIDE numbers [a, b, c, d, tx, ty] per bone, skeleton space (y-up). */
  world: Float64Array;
  /** Bone lengths in local units (a `SkeletonData.bones` array fits). */
  bones: readonly Pick<BoneData, 'length'>[];
  camera: Camera;
  /** 1 = pickable, anything else = skipped (hidden/locked bones); null = every bone. */
  pickable: Uint8Array | null;
}

/** The picked bone, which part was hit and the screen distance to it. */
export interface BoneHit {
  bone: number;
  /** Hit part: 'joint' for a snapped joint; otherwise joint > tip > body among the parts within tolerance. */
  part: 'joint' | 'tip' | 'body';
  /** Snapped joint: the joint distance; otherwise the true distance min(joint, tip, body) bones are ranked by. */
  distancePx: number;
}

const S = POSE_LAYOUT.WORLD_STRIDE;
const JOINT_SNAP = HANDLE_PX.jointSnap;

/** Screen distances from p to bone i's joint, tip and body (tip = Infinity for a bone of length <= 0). */
function distancesOf(input: BonePickInput, i: number, length: number, p: Vec2, scratch: [Vec2, Vec2]): [number, number, number] {
  const [o, t] = scratch;
  const w = input.world;
  const base = i * S;
  const tx = readNum(w, base + 4);
  const ty = readNum(w, base + 5);
  affine.apply(input.camera.worldToPixel, tx, ty, o);
  affine.apply(input.camera.worldToPixel, readNum(w, base) * length + tx, readNum(w, base + 2) * length + ty, t);
  const dTip = length > 0 ? trig.hypot(p[0] - t[0], p[1] - t[1]) : Infinity;
  return [trig.hypot(p[0] - o[0], p[1] - o[1]), dTip, pointSegmentDistance(p[0], p[1], o[0], o[1], t[0], t[1])];
}

/** R4-03 hit of bone i: labelled joint > tip > body among the parts within tolerance, at the true distance. */
function rankedHit(i: number, [dJoint, dTip, dBody]: [number, number, number]): BoneHit | null {
  const distancePx = Math.min(dJoint, dTip, dBody);
  if (dJoint <= HANDLE_PX.joint) return { bone: i, part: 'joint', distancePx };
  if (dTip <= HANDLE_PX.joint) return { bone: i, part: 'tip', distancePx };
  return dBody <= HANDLE_PX.bone ? { bone: i, part: 'body', distancePx } : null;
}

/** True when hit exists and is at least as near as best (equal distances go to the later bone). */
function nearer(hit: BoneHit | null, best: BoneHit | null): hit is BoneHit {
  return hit !== null && (best === null || hit.distancePx <= best.distancePx);
}

/**
 * Picks a bone (R16-01 over R4-03): the nearest joint within HANDLE_PX.jointSnap (3 px) wins over any tip or body hit;
 * otherwise the bone nearest by true distance wins. Equal distances go to the later bone (a child over its parent).
 */
export function pickBone(input: BonePickInput, sx: number, sy: number): BoneHit | null {
  const p: Vec2 = [sx, sy];
  const scratch: [Vec2, Vec2] = [[0, 0], [0, 0]];
  let best: BoneHit | null = null;
  let snap: BoneHit | null = null;
  for (const [i, bone] of input.bones.entries()) {
    if (input.pickable !== null && input.pickable[i] !== 1) continue;
    const d = distancesOf(input, i, bone.length, p, scratch);
    const joint: BoneHit | null = d[0] <= JOINT_SNAP ? { bone: i, part: 'joint', distancePx: d[0] } : null;
    if (nearer(joint, snap)) snap = joint;
    const hit = rankedHit(i, d);
    if (nearer(hit, best)) best = hit;
  }
  return snap ?? best;
}
